import { IError } from '@checkout/errors';
import { INotification } from '@checkout/notifications';
import { IAccountDetailsChangeRequestPayload } from '@checkout/payloadTypes';
import { ICountry, ICustomerData, ILanguage, ILoggedIn, IPhysicalStore, IState, RootState } from '@checkout/state';
import { Field, Form, Formik, FormikActions, InjectedFormikProps, yupToFormErrors } from 'formik';
import * as React from 'react';
import { ChangeEvent } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { FormikErrorFocus, NewsletterCheckbox } from '..';
import { ICheckoutContextConfig } from '../../../config/contextCheckoutConfig.d';
import messages from '../../../i18n/base-en';
import { accountDetailsChangeRequest, customerClubFetchRequest } from '../../../reducers/checkout';
import { getOptionsData } from '../../../utils/address';
import { getStatesItems } from '../../../utils/address';
import { Button, DropDownField, GroupTitle, InputField, IntlTelField, MembershipHeader, MessageBox, Section } from '../../presentational';
import * as Styles from './AccountDetailsForm.scss';
import getValidationSchema from './AccountDetailsFormValidationSchema';

interface IGender {
  name: string;
  gender: 'F' | 'M';
}

interface IOwnProps {
  checkoutContextConfig: ICheckoutContextConfig;
}

interface IConnectedProps {
  isMembershipFetching: boolean;
  loggedIn: ILoggedIn | null;
  customerClubData: ICustomerData;
  countries: ICountry[];
  errors?: IError;
  notifications?: INotification;
}

interface IConnectetDispatchProps {
  accountDetailsChangeRequest: typeof accountDetailsChangeRequest;
  customerClubFetchRequest: typeof customerClubFetchRequest;
}

interface IAccountValues {
  address1: string;
  address2: string;
  city: string;
  country: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  state: string;
  zipCode: string;
  newsletter: boolean;
  physicalStore?: string;
  gender?: 'M' | 'F';
  cus_att_birthdate?: string;
  language?: ILanguage;
  customerTypes?: string[];
}

interface IFormValues {
  account: IAccountValues;
}

type Props = IOwnProps & IConnectedProps & IConnectetDispatchProps & InjectedFormikProps<IOwnProps, IFormValues> & InjectedIntlProps;

class AccountDetailsForm extends React.Component<Props, {}> {

  validationSchema = () => {
    return getValidationSchema(this.props.intl);
  }

  validate = (values: IFormValues) => {
    const { checkoutContextConfig, customerClubData, countries } = this.props;
    const { physicalStores } = checkoutContextConfig.modules.membership.customerClubFields;
    const validationSchema = this.validationSchema();
    const statesList = getStatesItems(countries, values.account.country);

    const context: { isStateFieldMounted: boolean; isPhisicalStoreMounted: boolean; } = {
      isStateFieldMounted: statesList !== undefined,
      isPhisicalStoreMounted: customerClubData.customerClubFields !== undefined && physicalStores !== undefined,
    };

    return validationSchema.validate(values, { abortEarly: false, context })
      .catch(error => {
        // tslint:disable-next-line:no-console
        console.log(error);
        throw yupToFormErrors(error);
      });
  }

  onSubmit = (values: IFormValues, formikBag: FormikActions<IFormValues>) => {
    const { customerClubData } = this.props;
    const { physicalStore, email, cus_att_birthdate, ...accountValues } = values.account;
    let state = values.account.state;
    let customerClubFields = undefined;
    const stateAvailable = getStatesItems(this.props.countries, values.account.country);

    if (stateAvailable === null || stateAvailable === undefined) {
      state = '';
    }

    if (physicalStore !== '' && customerClubData.customerClubFields !== undefined) {
      customerClubFields = {
        primaryStore: physicalStore,
      };
    }

    const requestPayload: IAccountDetailsChangeRequestPayload = {
      ...accountValues,
      newEmail: email,
      state,
      customerClubFields,
    };

    if (cus_att_birthdate) {
      requestPayload.customAttributes = {
        cus_att_birthdate
      };
    }

    this.props.accountDetailsChangeRequest(requestPayload);
  }

  componentDidMount() {
    const { checkoutContextConfig, customerClubFetchRequest: fetchCustomerClubData } = this.props;

    if (checkoutContextConfig.modules.membership.customerClubFields.isEnabled) {
      fetchCustomerClubData();
    }
  }

  getInitialValues = (): IFormValues => {
    const { loggedIn, customerClubData } = this.props;
    const { customerTypes: _, language: __, ...restLoggedIn } = loggedIn || {} as IAccountValues;

    return {
      account: {
        ...restLoggedIn,
        physicalStore: customerClubData.customerClubFields && customerClubData.customerClubFields.primaryStore
      }
    };
  }

  getGenderList = (): IGender[] => {
    const { formatMessage } = this.props.intl;

    return [
      { gender: 'F', name: formatMessage(messages.membership_gender_female) },
      { gender: 'M', name: formatMessage(messages.membership_gender_male) }
    ];
  }

  render() {
    const { loggedIn, countries, intl, errors, notifications, checkoutContextConfig, customerClubData, isMembershipFetching } = this.props;
    const { customerClubFields } = customerClubData;
    const { physicalStores } = checkoutContextConfig.modules.membership.customerClubFields;
    const { formatMessage } = intl;

    if (loggedIn === null) {
      return null;
    }

    const { country } = loggedIn;

    return (
      <div className={Styles.wrapper}>
        <MembershipHeader title={formatMessage(messages.membership_personal_data_title)} subTitle={formatMessage(messages.membership_personal_data_subtitle)} />
        <Formik<IFormValues>
          initialValues={this.getInitialValues()}
          validate={this.validate}
          enableReinitialize={true}
          onSubmit={this.onSubmit}
          render={(formik) => {
            const countryOptions = getOptionsData('Country', countries, null);
            const stateOptions = getOptionsData('State', countries, formik.values.account.country);

            return (
              <Form className={Styles.form}>
                <Section>
                  <GroupTitle title={formatMessage(messages.membership_personal_information_group_title)} />
                  <div className={Styles.fields}>
                    <Field
                      id="account.firstName"
                      name="account.firstName"
                      component={InputField}
                      placeholder={formatMessage(messages.membership_firstname_placeholder)}
                      label={formatMessage(messages.membership_firstname_label)}
                      required={true}
                      type="text"
                      sizeVariant="half"
                      submitOnEnter={true}
                    />
                    <Field
                      id="account.lastName"
                      name="account.lastName"
                      component={InputField}
                      placeholder={formatMessage(messages.membership_lastname_placeholder)}
                      label={formatMessage(messages.membership_lastname_label)}
                      required={true}
                      type="text"
                      sizeVariant="half"
                      submitOnEnter={true}
                    />
                    <Field
                      id="account.gender"
                      name="account.gender"
                      component={DropDownField}
                      placeholder={formatMessage(messages.membership_gender_placeholder)}
                      label={formatMessage(messages.membership_gender_label)}
                      options={this.getGenderList()}
                      valueSelector={(item: IGender) => item.gender}
                      sizeVariant="half"
                      type="select"
                    />
                    {loggedIn.cus_att_birthdate !== undefined && (
                      <Field
                        id="account.cus_att_birthdate"
                        name="account.cus_att_birthdate"
                        component={InputField}
                        placeholder={formatMessage(messages.membership_dob_placeholder)}
                        label={formatMessage(messages.membership_dob_label)}
                        type="text"
                        sizeVariant="half"
                        submitOnEnter={true}
                      />
                    )}
                    {customerClubFields && physicalStores && (
                      <Field
                        id="account.physicalStore"
                        name="account.physicalStore"
                        component={DropDownField}
                        placeholder={formatMessage(messages.membership_physical_store_placeholder)}
                        label={formatMessage(messages.membership_physical_store_label)}
                        required={true}
                        options={checkoutContextConfig.modules.membership.customerClubFields.physicalStores}
                        valueSelector={(item: IPhysicalStore) => item.store}
                        type="select"
                      />
                    )}
                  </div>
                </Section>
                <Section>
                  <GroupTitle title={formatMessage(messages.shipping_address_group_title)} />
                  <div className={Styles.fields}>
                    <Field
                      id="account.address1"
                      name="account.address1"
                      component={InputField}
                      placeholder={formatMessage(messages.membership_address1_placeholder)}
                      label={formatMessage(messages.membership_address1_label)}
                      required={true}
                      type="text"
                      submitOnEnter={true}
                    />
                    <Field
                      id="account.address2"
                      name="account.address2"
                      component={InputField}
                      placeholder={formatMessage(messages.membership_address2_placeholder)}
                      label={formatMessage(messages.membership_address2_label)}
                      type="text"
                      submitOnEnter={true}
                    />
                    <Field
                      id="account.city"
                      name="account.city"
                      component={InputField}
                      placeholder={formatMessage(messages.membership_city_placeholder)}
                      label={formatMessage(messages.membership_city_label)}
                      required={true}
                      type="text"
                      sizeVariant="medium"
                      submitOnEnter={true}
                    />
                    <Field
                      id="account.zipCode"
                      name="account.zipCode"
                      component={InputField}
                      placeholder={formatMessage(messages.membership_zipcode_placeholder)}
                      label={formatMessage(messages.membership_zipcode_label)}
                      required={true}
                      type="text"
                      sizeVariant="small"
                      submitOnEnter={true}
                    />
                    <Field
                      id="account.country"
                      name="account.country"
                      component={DropDownField}
                      placeholder={formatMessage(messages.country_dropdown_placeholder)}
                      label={formatMessage(messages.country_label)}
                      required={true}
                      sizeVariant="medium"
                      options={countryOptions}
                      valueSelector={(item: ICountry) => item.country}
                      type="select"
                      onChange={(event: ChangeEvent<any>) => {
                        if (stateOptions) {
                          formik.setFieldValue('account.state', '');
                        }
                      }}
                    />
                    {stateOptions && (
                      <Field
                        id="account.state"
                        name="account.state"
                        component={DropDownField}
                        placeholder={formatMessage(messages.state_dropdown_placeholder)}
                        label={formatMessage(messages.state_label)}
                        required={true}
                        sizeVariant="small"
                        options={stateOptions}
                        valueSelector={(item: IState) => item.state}
                        type="select"
                      />
                    )}
                  </div>
                </Section>
                <Section>
                  <GroupTitle title={formatMessage(messages.contact_group_title)} />
                  <div className={Styles.fields}>
                    <Field
                      id="account.email"
                      name="account.email"
                      component={InputField}
                      placeholder={formatMessage(messages.membership_email_placeholder)}
                      label={formatMessage(messages.membership_email_label)}
                      required={true}
                      type="email"
                      sizeVariant="medium"
                      submitOnEnter={true}
                    />
                    <Field
                      id="account.phoneNumber"
                      name="account.phoneNumber"
                      component={checkoutContextConfig.ui.intlTelInput.isEnabled ? IntlTelField : InputField}
                      placeholder={formatMessage(messages.membership_phone_placeholder)}
                      label={formatMessage(messages.membership_phone_label)}
                      required={true}
                      type="tel"
                      sizeVariant="small"
                      country={country}
                    />
                  </div>
                </Section>
                <Section>
                  <GroupTitle title={formatMessage(messages.consent_group_title)} />
                  <NewsletterCheckbox
                    id="account.newsletter"
                    name="account.newsletter"
                    checkoutContextConfig={checkoutContextConfig}
                  />
                </Section>

                {errors && errors.uiType === 'inline' && <MessageBox spacingBottom={true} type="error" message={errors.messages[0]} />}
                {notifications && <MessageBox spacingBottom={true} type={notifications.type} message={notifications.messages[0]} />}

                <div className={Styles.button}>
                  <Button
                    type="submit"
                    variant="primary"
                    align="center"
                    size="medium"
                    width="long"
                    label={formatMessage(messages.membership_update)}
                    isLoading={isMembershipFetching}
                  />
                </div>

                <FormikErrorFocus formikContext={formik} />
              </Form>
            );
          }}
        />
      </div>
    );
  }
}

export default compose<React.ComponentClass<IOwnProps>>(
  injectIntl,
  connect<IConnectedProps, IConnectetDispatchProps>(
    (state: RootState) => ({
      isMembershipFetching: state.checkout.isMembershipFetching,
      loggedIn: state.checkout.loggedIn,
      customerClubData: state.checkout.customerClubData,
      countries: state.checkout.countries,
      errors: state.checkout.errors,
      notifications: state.checkout.notifications,
    }),
    {
      accountDetailsChangeRequest,
      customerClubFetchRequest,
    },
  )
)(AccountDetailsForm);
